/**
 * @file The MarketplaceListComponent is used for displaying Shifts and Projects with open slots
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { TranslateService } from '@ngx-translate/core';
import { ListBase } from '../../base/list/list.base';
import { DialogService } from '../../core/dialog.service';
import { AlertsService } from '../../core/alerts.service';
import { DomainService } from '../../domain.service';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalStorageService } from '../../core/local-storage.service';
import { OverlayService } from '../../core/overlay.service';
import { EventsService } from '../../core/event.service';
import { GridService } from '../../grids/grid.service';
import { UserService } from '../../user.service';
import { ActionService } from '../../core/action.service';
/**
 * The MarketplaceListComponent is used to show the open slots for Shifts and Projects (Marketplace)
 *
 * @export
 * @class MarketplaceListComponent
 * @extends {ListBase}
 */
var MarketplaceListComponent = /** @class */ (function (_super) {
    tslib_1.__extends(MarketplaceListComponent, _super);
    /**
     * Constructs a MarketplaceListComponent
     * @param configurationsService Service to get the configuration of this component
     * @param domainService Service to load and save entity data
     * @param dialogService Service for displaying dialogs
     * @param alertsService Service for publish alerts
     * @param router Service for routing events
     */
    function MarketplaceListComponent(userService, domainService, dialogService, alertsService, translateService, router, overlayService, route, localStorageService, eventsService, gridService, actionService) {
        var _this = 
        // TODO: introduceren van marketplace service!
        _super.call(this, userService, domainService, dialogService, alertsService, translateService, router, overlayService, localStorageService, eventsService, actionService) || this;
        _this.route = route;
        _this.gridService = gridService;
        /**
         * A value indicating whether the query should exclude tasks whose requirements are not met by the user
         */
        _this._requirements = _this.localStorageService.readProperty('local', 'marketplace-requirements', true, [true, false]);
        /**
         * A value indicating whether the query should exclude tasks for which the the user is unavailable
         */
        _this._availability = _this.localStorageService.readProperty('local', 'marketplace-availability', true, [true, false]);
        /**
         * A value indicating whether the query should exclude tasks without unassigned deployments
         */
        _this._openSlots = _this.localStorageService.readProperty('local', 'marketplace-openslots', true, [true, false]);
        // This subscription will be called every time the user clicks on one of the three checkboxes
        _this.subscriptionPool.add(_this.route.queryParams.subscribe(function (params) {
            var doRefresh = false;
            if (params['requirements'] !== undefined) {
                var value = params['requirements'] === 'true';
                if (_this._requirements !== value) {
                    _this._requirements = value;
                    _this.localStorageService.updateProperty('local', 'marketplace-requirements', value, [true, false]);
                    doRefresh = true;
                }
            }
            if (params['availability'] !== undefined) {
                var value = params['availability'] === 'true';
                if (_this._availability !== value) {
                    _this._availability = value;
                    _this.localStorageService.updateProperty('local', 'marketplace-availability', value, [true, false]);
                    doRefresh = true;
                }
            }
            if (params['openslots'] !== undefined) {
                var value = params['openslots'] === 'true';
                if (_this._openSlots !== value) {
                    _this._openSlots = value;
                    _this.localStorageService.updateProperty('local', 'marketplace-openslots', value, [true, false]);
                    doRefresh = true;
                }
            }
            if (doRefresh) {
                _this.refresh();
            }
        }));
        _this.subscriptionPool.add(_this.gridService.stateChanged.subscribe(function () {
            _this.refresh();
        }));
        return _this;
    }
    Object.defineProperty(MarketplaceListComponent.prototype, "start", {
        /**
         * Gets the horizon date
         */
        get: function () {
            return this.gridService.startDate;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MarketplaceListComponent.prototype, "finish", {
        /**
         * Gets the horizon date
         */
        get: function () {
            return this.gridService.endDate;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MarketplaceListComponent.prototype, "requirements", {
        /**
         * Get the requirements options
         * @type {boolean}
         */
        get: function () {
            return this._requirements;
        },
        /**
         * Sets the requirement boolean value in the URL
         */
        set: function (requirement) {
            this.setQueryParams(requirement, this._availability, this._openSlots);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MarketplaceListComponent.prototype, "canActions", {
        get: function () {
            return false; // Used to hide the 'select items' functionality because there are 'column actions', see: #3434
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MarketplaceListComponent.prototype, "availability", {
        /**
         * Get the availability options
         * @type {boolean}
         */
        get: function () {
            return this._availability;
        },
        /**
         * Sets the availability boolean value in the URL
         */
        set: function (availability) {
            this.setQueryParams(this._requirements, availability, this._openSlots);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MarketplaceListComponent.prototype, "openSlots", {
        /**
         * Get the open slots options
         * @type {boolean}
         */
        get: function () {
            return this._openSlots;
        },
        /**
         * Sets the openslots boolean value in the URL
         */
        set: function (openSlots) {
            this.setQueryParams(this._requirements, this._availability, openSlots);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Is called after the block name is set and the configuration is loaded.
     */
    MarketplaceListComponent.prototype.afterInitialize = function () {
        var _this = this;
        _super.prototype.afterInitialize.call(this);
        /*
            Fix #3520: De action werd eerst op lijst configuratie niveau meegeven.
            In Angelar niet stabiel: Inject parent compoment in child compoment.
            Wel zo gelaten omdat anders config task ook aangepast zou moeten worden.
        */
        var marketColumns = this.configuration.data.fields.filter(function (fld) { return fld.editor != null && fld.editor.indexOf('marketplace-') !== -1; });
        if (marketColumns.length === 0) {
            return;
        }
        if (this.configuration.actions == null) {
            throw new Error('Marketplace action is missing in list configuration.');
        }
        marketColumns.forEach(function (column) {
            var action = null;
            if (column.format == null) {
                switch (column.editor) {
                    case 'marketplace-service':
                        action = _this.configuration.actions.find(function (a) { return a.name === 'open-overlay-service'; });
                        if (action == null) {
                            throw new Error('Marketplace action is missing in list configuration.');
                        }
                        column.format = action;
                        break;
                    case 'marketplace-demand':
                        action = _this.configuration.actions.find(function (a) { return a.name === 'open-overlay-demand'; });
                        if (action == null) {
                            throw new Error('Marketplace action is missing in list configuration.');
                        }
                        column.format = action;
                        break;
                    default:
                        console.log("Unknown marketplace editor: " + column.editor);
                        break;
                }
            }
        });
    };
    /**
     * Sets the URL parameters
     */
    MarketplaceListComponent.prototype.setQueryParams = function (requirements, availability, openSlots) {
        var queryParams = {
            requirements: requirements,
            availability: availability,
            openslots: openSlots
        };
        this.router.navigate([], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: queryParams
        });
    };
    return MarketplaceListComponent;
}(ListBase));
export { MarketplaceListComponent };
